import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, Button, Link, Stack, TextField } from '@mui/material';
import { useAuthStore } from './authStore';
import { fetchCurrentUser, resendOtp, verifyOtp } from '../api/endpoints/auth';
import { ApiError } from '../api/types/common';
import { AuthLayout } from './AuthLayout';

/** OTP / 2FA second-factor screen (legacy `Session/Verify`). Reached only when `Login`
 *  responded with `requiresOtp` — see LoginPage + authStore's `awaiting-otp` status. */
export default function OtpVerifyPage() {
  const navigate = useNavigate();
  const pendingOtp = useAuthStore((s) => s.pendingOtp);
  const status = useAuthStore((s) => s.status);
  const setSession = useAuthStore((s) => s.setSession);
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);
  const logout = useAuthStore((s) => s.logout);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);

  if (status !== 'awaiting-otp' || !pendingOtp) {
    return <Navigate to="/login" replace />;
  }

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await verifyOtp({ sessionId: pendingOtp.sessionId, code });
      const sessionId = res.sessionId ?? pendingOtp.sessionId;
      const sessionToken = res.sessionToken;
      if (!sessionToken) throw new ApiError('Doğrulama tamamlanamadı.');
      setSession({ sessionId, sessionToken }, pendingOtp.employeeId);
      setCurrentUser(await fetchCurrentUser());
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kod doğrulanamadı.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      heading="Doğrulama Kodu"
      subheading={
        pendingOtp.maskedEmail
          ? `${pendingOtp.maskedEmail} adresine gönderilen 6 haneli kodu girin.`
          : 'E-postanıza gönderilen 6 haneli kodu girin.'
      }
      footer={
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Link
            component="button"
            variant="body2"
            onClick={async () => {
              await resendOtp(pendingOtp.sessionId).catch(() => undefined);
              setResent(true);
            }}
          >
            Kodu tekrar gönder
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              logout();
              navigate('/login', { replace: true });
            }}
          >
            Girişe dön
          </Link>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {resent && <Alert severity="success">Kod tekrar gönderildi.</Alert>}
        <TextField
          label="Kod"
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6 } }}
          autoFocus
        />
        <Button variant="contained" size="large" fullWidth onClick={submit} disabled={busy || code.length < 4}>
          {busy ? 'Doğrulanıyor…' : 'Doğrula'}
        </Button>
      </Stack>
    </AuthLayout>
  );
}
